/**
 * HostDashboardPage Component
 *
 * Main dashboard overview for hosts showing stats, listings, and bookings.
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { HostLayout } from './components/HostLayout';
import { useHostStats, useHostListings, useHostBookings } from './hooks';
import { useAuth } from '../../contexts';

export const HostDashboardPage: React.FC = () => {
  const { user } = useAuth();
  const { stats, isLoading: statsLoading } = useHostStats();
  const { listings, isLoading: listingsLoading } = useHostListings();
  const { bookings, isLoading: bookingsLoading, counts } = useHostBookings('upcoming');

  return (
    <HostLayout>
      {/* Welcome Section */}
      <div className="mb-8">
        <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
          Welcome back, {user?.displayName?.split(' ')[0] || 'Host'}!
        </h1>
        <p className="text-gray-600">
          Here's an overview of your hosting activity.
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6 lg:mb-8">
        {statsLoading ? (
          [...Array(4)].map((_, i) => (
            <div key={i} className="animate-pulse bg-gray-100 rounded-xl h-28" />
          ))
        ) : (
          <>
            <StatCard
              label="Active Listings"
              value={stats?.activeListings || 0}
              icon={<BuildingIcon className="w-5 h-5" />}
              color="blue"
              href="/host/listings"
            />
            <StatCard
              label="Pending Requests"
              value={counts.pending}
              icon={<ClockIcon className="w-5 h-5" />}
              color="amber"
              href="/host/bookings?tab=pending"
              highlight={counts.pending > 0}
            />
            <StatCard
              label="Upcoming Check-ins"
              value={stats?.upcomingCheckins || 0}
              icon={<CalendarIcon className="w-5 h-5" />}
              color="green"
              href="/host/bookings?tab=upcoming"
            />
            <StatCard
              label="This Month"
              value={`$${(stats?.monthlyEarnings || 0).toLocaleString()}`}
              icon={<DollarIcon className="w-5 h-5" />}
              color="purple"
              href="/host/earnings"
              subtext={stats && stats.lastMonthEarnings > 0 ? (
                <span className={stats.monthlyEarnings > stats.lastMonthEarnings ? 'text-green-600' : 'text-red-600'}>
                  {stats.monthlyEarnings > stats.lastMonthEarnings ? '+' : ''}
                  {Math.round(((stats.monthlyEarnings - stats.lastMonthEarnings) / stats.lastMonthEarnings) * 100)}%
                </span>
              ) : undefined}
            />
          </>
        )}
      </div>

      {/* Two Column Layout */}
      <div className="grid lg:grid-cols-3 gap-6 lg:gap-8">
        {/* Left Column - Larger */}
        <div className="lg:col-span-2 space-y-6 lg:space-y-8">
          {/* Pending Booking Requests */}
          {counts.pending > 0 && (
            <section className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-amber-800">
                  Action Required
                </h2>
                <Link
                  to="/host/bookings?tab=pending"
                  className="text-sm text-amber-700 hover:text-amber-900 font-medium"
                >
                  View all
                </Link>
              </div>
              <p className="text-amber-700">
                You have {counts.pending} pending booking request{counts.pending !== 1 ? 's' : ''} waiting for your response.
              </p>
              <Link
                to="/host/bookings?tab=pending"
                className="inline-flex items-center gap-2 mt-4 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors"
              >
                Review Requests
                <ArrowRightIcon className="w-4 h-4" />
              </Link>
            </section>
          )}

          {/* Upcoming Bookings */}
          <section>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Upcoming Bookings</h2>
              <Link
                to="/host/bookings"
                className="text-sm text-primary-600 hover:text-primary-700 font-medium"
              >
                View all
              </Link>
            </div>

            {bookingsLoading ? (
              <div className="space-y-3">
                {[1, 2].map((i) => (
                  <div key={i} className="animate-pulse bg-gray-100 rounded-xl h-24" />
                ))}
              </div>
            ) : bookings.length > 0 ? (
              <div className="space-y-3">
                {bookings.slice(0, 5).map((booking) => (
                  <BookingCard key={booking.id} booking={booking} />
                ))}
              </div>
            ) : (
              <div className="text-center py-12 bg-gray-50 rounded-xl">
                <CalendarIcon className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">No upcoming bookings</p>
              </div>
            )}
          </section>
        </div>

        {/* Right Column - Sidebar */}
        <div className="space-y-6">
          {/* Listings Summary */}
          <section className="bg-white rounded-xl border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-semibold text-gray-900">Your Listings</h2>
              <Link
                to="/host/listings"
                className="text-sm text-primary-600 hover:text-primary-700"
              >
                Manage
              </Link>
            </div>

            {listingsLoading ? (
              <div className="space-y-3">
                {[1, 2].map((i) => (
                  <div key={i} className="animate-pulse bg-gray-100 rounded h-16" />
                ))}
              </div>
            ) : listings.length > 0 ? (
              <div className="space-y-3">
                {listings.slice(0, 3).map((listing) => (
                  <ListingItem key={listing.id} listing={listing} />
                ))}
                {listings.length > 3 && (
                  <Link
                    to="/host/listings"
                    className="block text-center text-sm text-gray-500 hover:text-gray-700 pt-2"
                  >
                    +{listings.length - 3} more listings
                  </Link>
                )}
              </div>
            ) : (
              <div className="text-center py-6">
                <p className="text-gray-500 mb-4">No listings yet</p>
                <Link
                  to="/host/listings/new"
                  className="text-primary-600 hover:text-primary-700 font-medium"
                >
                  Add your first listing
                </Link>
              </div>
            )}
          </section>

          {/* Wallet snapshot */}
          <section className="bg-white rounded-xl border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-semibold text-gray-900">Wallet</h2>
              <Link
                to="/host/earnings"
                className="text-sm text-primary-600 hover:text-primary-700"
              >
                Details
              </Link>
            </div>

            {statsLoading ? (
              <div className="animate-pulse space-y-4">
                <div className="h-8 bg-gray-100 rounded w-1/2" />
                <div className="h-4 bg-gray-100 rounded w-3/4" />
              </div>
            ) : (
              <>
                <div className="mb-4">
                  <p className="text-sm text-gray-500">Available for payout</p>
                  <p className="text-2xl font-bold text-gray-900">
                    ${(stats?.availablePayout || 0).toLocaleString()}
                  </p>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Pending</span>
                  <span className="text-gray-900">
                    ${(stats?.pendingPayout || 0).toLocaleString()}
                  </span>
                </div>
              </>
            )}
          </section>

          {/* Quick Actions */}
          <section className="bg-white rounded-xl border border-gray-200 p-6">
            <h2 className="font-semibold text-gray-900 mb-4">Quick Actions</h2>
            <div className="space-y-2">
              <QuickAction
                icon={<PlusIcon className="w-5 h-5" />}
                label="Add New Listing"
                href="/host/listings/new"
              />
              <QuickAction
                icon={<CalendarIcon className="w-5 h-5" />}
                label="Manage Calendar"
                href="/host/calendar"
              />
              <QuickAction
                icon={<MessageIcon className="w-5 h-5" />}
                label="View Messages"
                href="/host/messages"
              />
            </div>
          </section>
        </div>
      </div>
    </HostLayout>
  );
};

// Stat Card
interface StatCardProps {
  label: string;
  value: string | number;
  icon: React.ReactNode;
  color: 'blue' | 'amber' | 'green' | 'purple';
  href: string;
  highlight?: boolean;
  subtext?: React.ReactNode;
}

const colorClasses = {
  blue: 'bg-blue-50 text-blue-600',
  amber: 'bg-amber-50 text-amber-600',
  green: 'bg-green-50 text-green-600',
  purple: 'bg-purple-50 text-purple-600',
};

const StatCard: React.FC<StatCardProps> = ({
  label,
  value,
  icon,
  color,
  href,
  highlight,
  subtext,
}) => (
  <Link
    to={href}
    className={`block p-5 rounded-xl transition-shadow hover:shadow-md ${
      highlight
        ? 'bg-amber-50 border-2 border-amber-300'
        : 'bg-white border border-gray-200'
    }`}
  >
    <div className="flex items-center justify-between mb-3">
      <span className="text-sm text-gray-500">{label}</span>
      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${colorClasses[color]}`}>
        {icon}
      </div>
    </div>
    <p className="text-2xl font-bold text-gray-900">{value}</p>
    {subtext && <p className="text-xs mt-1">{subtext}</p>}
  </Link>
);

// Booking Card
interface BookingCardProps {
  booking: {
    id: string;
    listingTitle: string;
    listingPhoto: string;
    guest: { name: string; photo?: string };
    checkIn: Date;
    checkOut: Date;
    nights: number;
    status: string;
    pricing: { hostPayout: number };
  };
}

const formatDate = (date: Date): string =>
  date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

const BookingCard: React.FC<BookingCardProps> = ({ booking }) => (
  <Link
    to={`/host/bookings/${booking.id}`}
    className="flex items-center gap-4 p-4 bg-white border border-gray-200 rounded-xl hover:shadow-md transition-shadow"
  >
    <img
      src={booking.listingPhoto}
      alt={booking.listingTitle}
      className="w-16 h-16 rounded-lg object-cover"
    />
    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-2 mb-1">
        {booking.guest.photo ? (
          <img
            src={booking.guest.photo}
            alt={booking.guest.name}
            className="w-6 h-6 rounded-full object-cover"
          />
        ) : (
          <div className="w-6 h-6 bg-gray-200 rounded-full flex items-center justify-center text-xs">
            {booking.guest.name.charAt(0)}
          </div>
        )}
        <span className="font-medium text-gray-900 truncate">{booking.guest.name}</span>
      </div>
      <p className="text-sm text-gray-500 truncate">{booking.listingTitle}</p>
      <p className="text-sm text-gray-600">
        {formatDate(booking.checkIn)} - {formatDate(booking.checkOut)} · {booking.nights} nights
      </p>
    </div>
    <div className="text-right">
      <p className="font-semibold text-gray-900">${booking.pricing.hostPayout}</p>
    </div>
  </Link>
);

// Listing Item
interface ListingItemProps {
  listing: {
    id: string;
    title: string;
    photo: string;
    status: string;
  };
}

const statusColors: Record<string, string> = {
  active: 'bg-green-100 text-green-700',
  paused: 'bg-gray-100 text-gray-700',
  draft: 'bg-amber-100 text-amber-700',
};

const ListingItem: React.FC<ListingItemProps> = ({ listing }) => (
  <Link
    to={`/host/listings/${listing.id}`}
    className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50 transition-colors"
  >
    <img
      src={listing.photo}
      alt={listing.title}
      className="w-12 h-12 rounded-lg object-cover"
    />
    <div className="flex-1 min-w-0">
      <p className="font-medium text-gray-900 text-sm truncate">{listing.title}</p>
      <div className="flex items-center gap-2 mt-0.5">
        <span className={`text-xs px-2 py-0.5 rounded-full capitalize ${statusColors[listing.status] || 'bg-gray-100'}`}>
          {listing.status}
        </span>
      </div>
    </div>
  </Link>
);

// Quick Action
interface QuickActionProps {
  icon: React.ReactNode;
  label: string;
  href: string;
}

const QuickAction: React.FC<QuickActionProps> = ({ icon, label, href }) => (
  <Link
    to={href}
    className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 transition-colors"
  >
    <div className="text-gray-400">{icon}</div>
    <span className="text-sm text-gray-700">{label}</span>
    <ArrowRightIcon className="w-4 h-4 text-gray-400 ml-auto" />
  </Link>
);

// Icons
const BuildingIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
  </svg>
);

const ClockIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const CalendarIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
  </svg>
);

const DollarIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const PlusIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
  </svg>
);

const MessageIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
  </svg>
);

const ArrowRightIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
  </svg>
);

export default HostDashboardPage;
